import { filterPii } from './filters';
import { EventPayload, SendEvent } from './types';

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Sentry?: {
      captureException: (error: Error | string) => void;
      addBreadcrumb: ({
        message,
        category,
        level
      }: {
        message: string;
        category: string;
        level: string;
      }) => void;
      setContext: (context: string, data: Record<string, unknown>) => void;
      setExtras: (data: Record<string, unknown>) => void;
    };
  }
}

/**
 * Determine if Sentry is fully loaded and ready to use. Because
 *   Sentry is loaded asynchronously, the Sentry object may available
 *   before all the functions are defined.
 */
const isSentryReady = () =>
  window.Sentry &&
  typeof window.Sentry.captureException === 'function' &&
  typeof window.Sentry.setContext === 'function' &&
  typeof window.Sentry.setExtras === 'function';

/** Place to store Sentry operations until Sentry is ready */
const sentryQueue: (() => void)[] = [];

/**
 * Run all the functions currently in the sentry queue, then
 *   empty the queue.
 */
const flushSentryQueue = () => {
  if (!sentryQueue.length) {
    return;
  }
  for (const fn of sentryQueue) {
    try {
      fn();
    } catch (e) {
      console.error('Error while flushing Sentry queue', e);
    }
  }
  // Empty the queue
  sentryQueue.length = 0;
};

/**
 * If sentry is ready, calls the function.
 *
 * Otherwise, pushes the function to the queue to be executed later.
 */
const queueSentryAction = (fn?: () => void) => {
  // If Sentry is not ready, push the function to the queue. If Sentry
  //   is ready, flush the queue and call the function.
  if (isSentryReady()) {
    // If Sentry is ready, flush the queue and call the function
    flushSentryQueue();
    fn?.();
  } else if (fn) {
    sentryQueue.push(fn);
  }
};

/**
 * Source: https://docs.sentry.io/platforms/javascript/usage/
 *   If Sentry is successfully installed, this function will forward
 *   errors to Sentry, and decorate the context with any identifying
 *   data.
 * Because Sentry is loaded asynchronously, we need to queue up
 *   any events until Sentry is ready. This is done by checking
 *   if the Sentry object is defined and has the expected methods.
 * If Sentry is not ready, we push the function to the queue
 *   and flush the queue when Sentry is ready.
 */
export const sendToSentry: SendEvent = (payload: EventPayload) => {
  // Determine the function to call based on the payload method
  //   and the properties passed in.
  let fn: (() => void) | undefined;
  if (payload.method === 'error') {
    fn = () => {
      console.error('Sending to sentry', payload.error);
      window.Sentry?.captureException(payload.error);
    };
  } else if (payload.method === 'identify' && payload.accountId) {
    fn = () =>
      // Update the context in Sentry to include the user's accountId
      //   Any errors raised from this point forward will include the
      //   accountId in the context.
      window.Sentry?.setContext('user', {
        accountId: payload.accountId,
        ...filterPii(payload.properties)
      });
  } else if (payload.method === 'set-user-properties') {
    fn = () =>
      // Update the sentry context to include the user's properties
      window.Sentry?.setExtras({ ...filterPii(payload.properties) });
  }

  queueSentryAction(fn);
};

/**
 * This logs a "breadcrub", documentation:
 *  https://docs.sentry.io/platforms/javascript/enriching-events/breadcrumbs/\
 *
 * In short, this is like adding a log statement that will appear in the
 *  breadcrumbs of a sentry alert.
 */
export const captureBreadcrumb = ({
  message,
  category
}: {
  message: string;
  category?: string;
}) => {
  // Default category, during testing it would not show up without a category
  const fn = () =>
    window.Sentry?.addBreadcrumb?.({ message, category: category ?? 'debugging', level: 'info' });
  queueSentryAction(fn);
};
